import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { TaskOccurrence } from '../../../core/api/rtk/taskTemplatesApi';

interface SnoozeOccurrenceModalProps {
  isOpen: boolean;
  occurrence: TaskOccurrence | null;
  onClose: () => void;
  onSubmit: (reason: string) => Promise<void> | void;
  isSubmitting?: boolean;
}

// Mirror of views/tasks/containers/SnoozeTaskModal.tsx, scoped to the
// TaskTemplate arch. Asks for a required "reason" (notes) and forwards it to
// the parent, which is expected to call setOccurrenceStatusById({ status:
// 'Snoozed', notes }). The legacy "Create new linked task" checkbox is
// intentionally omitted — its commented-out cousin in the legacy modal has
// been dark for a while and creating a follow-up project mid-snooze is
// better handled from the Projects tab now that it exists on v2.
const SnoozeOccurrenceModal = ({
  isOpen,
  occurrence,
  onClose,
  onSubmit,
  isSubmitting = false,
}: SnoozeOccurrenceModalProps): JSX.Element => {
  const intl = useIntl();
  const [reason, setReason] = useState<string>('');
  const taskName = occurrence?.task?.name || occurrence?.name || '';
  const handleConfirm = async (): Promise<void> => {
    const trimmed = reason.trim();
    if (!trimmed) return;
    await onSubmit(trimmed);
    setReason('');
  };
  const handleClose = (): void => {
    setReason('');
    onClose();
  };
  return (
    <Modal isOpen={isOpen} toggle={handleClose} className="enhanced-dialog narrow padded-minimal">
      <ModalHeader toggle={handleClose}>
        {intl.formatMessage({ id: 'blockTask', defaultMessage: 'Block task' })}
      </ModalHeader>
      <ModalBody>
        <div className="snooze-modal">
          {taskName && <p>{taskName}</p>}
          <p>
            {intl.formatMessage({
              id: 'enterBlockReason',
              defaultMessage: 'Enter a reason for blocking this task.',
            })}
          </p>
          <div className="form-group">
            <textarea
              name="reason"
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              autoFocus
            />
          </div>
        </div>
      </ModalBody>
      <ModalFooter className="full-width">
        <Button className="submit-button delete" color="secondary" onClick={handleClose}>
          {intl.formatMessage({ id: 'cancel', defaultMessage: 'Cancel' })}
        </Button>
        <Button
          className="submit-button confirm"
          color="primary"
          onClick={handleConfirm}
          disabled={reason.trim() === '' || isSubmitting}
        >
          {isSubmitting
            ? intl.formatMessage({ id: 'saving', defaultMessage: 'Saving…' })
            : intl.formatMessage({ id: 'blockTask', defaultMessage: 'Block task' })}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SnoozeOccurrenceModal;
